/**
 * SMS segment counting (GSM 03.38 / 3GPP TS 23.038).
 *
 * This is the single source of truth for segment counts. The compose box uses it for the
 * live counter and the worker uses it to store `messages.segments`. Never reimplement it.
 *
 * - GSM-7: 160 septets in one segment, 153 per segment when concatenated.
 *   Extension-table characters ( ^ { } \ [ ~ ] | € form-feed ) take 2 septets and are never
 *   split across segments.
 * - UCS-2: used as soon as one character is outside GSM-7. 70 UTF-16 code units in one
 *   segment, 67 per segment when concatenated. Surrogate pairs (most emoji) take 2 units and
 *   are never split across segments.
 */

export type SmsEncoding = 'gsm7' | 'ucs2';

export interface SegmentInfo {
  encoding: SmsEncoding;
  /** Number of segments billed. 0 for an empty message. */
  segments: number;
  /** Septets for GSM-7, UTF-16 code units for UCS-2. */
  units: number;
  /** Capacity of each segment at the current size: 160/153 (GSM-7) or 70/67 (UCS-2). */
  unitsPerSegment: number;
  /** Units still free in the last segment before another segment is added. */
  remaining: number;
  /** Number of characters (Unicode code points). */
  characters: number;
  /** Characters that force UCS-2, unique, in order of first appearance. */
  nonGsmCharacters: string[];
}

const GSM_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM_EXTENDED = '\f^{}\\[~]|€';

const BASIC = new Set(Array.from(GSM_BASIC));
const EXTENDED = new Set(Array.from(GSM_EXTENDED));

export const GSM7_SINGLE = 160;
export const GSM7_MULTI = 153;
export const UCS2_SINGLE = 70;
export const UCS2_MULTI = 67;

/** Septet cost of one character in GSM-7, or null if it cannot be encoded in GSM-7. */
export function gsmSeptets(char: string): 1 | 2 | null {
  if (BASIC.has(char)) return 1;
  if (EXTENDED.has(char)) return 2;
  return null;
}

/** Splits a list of per-character costs into segments of `size`, never splitting a character. */
function packSegments(costs: number[], size: number): { segments: number; lastUsed: number } {
  let segments = 1;
  let used = 0;
  for (const cost of costs) {
    if (used + cost > size) {
      segments += 1;
      used = 0;
    }
    used += cost;
  }
  return { segments, lastUsed: used };
}

export function countSegments(text: string): SegmentInfo {
  const chars = Array.from(text);
  const nonGsm: string[] = [];
  const seen = new Set<string>();
  const septetCosts: number[] = [];

  for (const char of chars) {
    const cost = gsmSeptets(char);
    if (cost === null) {
      if (!seen.has(char)) {
        seen.add(char);
        nonGsm.push(char);
      }
    } else {
      septetCosts.push(cost);
    }
  }

  if (chars.length === 0) {
    return {
      encoding: 'gsm7',
      segments: 0,
      units: 0,
      unitsPerSegment: GSM7_SINGLE,
      remaining: GSM7_SINGLE,
      characters: 0,
      nonGsmCharacters: [],
    };
  }

  const encoding: SmsEncoding = nonGsm.length === 0 ? 'gsm7' : 'ucs2';
  const costs = encoding === 'gsm7' ? septetCosts : chars.map((c) => c.length);
  const units = costs.reduce((sum, c) => sum + c, 0);
  const single = encoding === 'gsm7' ? GSM7_SINGLE : UCS2_SINGLE;
  const multi = encoding === 'gsm7' ? GSM7_MULTI : UCS2_MULTI;

  if (units <= single) {
    return {
      encoding,
      segments: 1,
      units,
      unitsPerSegment: single,
      remaining: single - units,
      characters: chars.length,
      nonGsmCharacters: nonGsm,
    };
  }

  const { segments, lastUsed } = packSegments(costs, multi);
  return {
    encoding,
    segments,
    units,
    unitsPerSegment: multi,
    remaining: multi - lastUsed,
    characters: chars.length,
    nonGsmCharacters: nonGsm,
  };
}

/**
 * Look-alike characters that silently force UCS-2 (typically pasted from Word, Google Docs or
 * a phone keyboard), mapped to plain GSM-7 equivalents.
 */
export const GSM_REPLACEMENTS: Readonly<Record<string, string>> = {
  '\u2018': "'", // left single quote
  '\u2019': "'", // right single quote / apostrophe
  '\u201A': "'", // single low quote
  '\u201B': "'", // single reversed quote
  '\u2032': "'", // prime
  '\u0060': "'", // backtick
  '\u00B4': "'", // acute accent
  '\u2039': "'", // single left angle quote
  '\u203A': "'", // single right angle quote
  '\u201C': '"', // left double quote
  '\u201D': '"', // right double quote
  '\u201E': '"', // double low quote
  '\u201F': '"', // double reversed quote
  '\u2033': '"', // double prime
  '\u00AB': '"', // left guillemet
  '\u00BB': '"', // right guillemet
  '\u2010': '-', // hyphen
  '\u2011': '-', // non-breaking hyphen
  '\u2012': '-', // figure dash
  '\u2013': '-', // en dash
  '\u2014': '-', // em dash
  '\u2015': '-', // horizontal bar
  '\u2212': '-', // minus sign
  '\u2026': '...', // ellipsis
  '\u2022': '-', // bullet
  '\u00B7': '-', // middle dot
  '\u00A0': ' ', // no-break space
  '\u2002': ' ', // en space
  '\u2003': ' ', // em space
  '\u2009': ' ', // thin space
  '\u202F': ' ', // narrow no-break space
  '\u0009': ' ', // tab
  '\u200B': '', // zero-width space
  '\u200C': '', // zero-width non-joiner
  '\u200D': '', // zero-width joiner
  '\uFEFF': '', // byte order mark
};

/** Replaces look-alike characters with GSM-7 equivalents. Emoji and other characters are kept. */
export function toGsmSafe(text: string): { text: string; replaced: number } {
  let replaced = 0;
  let out = '';
  for (const char of text) {
    const replacement = GSM_REPLACEMENTS[char];
    if (replacement === undefined) {
      out += char;
    } else {
      out += replacement;
      replaced += 1;
    }
  }
  return { text: out, replaced };
}
